import { Fragment, useEffect, useRef, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import CompositeColorPicker from "@/appComponents/Blocks/Composites/CompositeColorPicker/CompositeColorPicker";
import { appstr } from "@coreapp/utils/appstr";
import { BuildSystemFieldOptions } from "@/Utils/DataLib";
import {
  Checkbox,
  ColorTrigger,
  Select,
  ToggleButtonGroup,
  ToggleButton
} from "@theorgchartdev/storybook";
import { enumUpdateProperty, kFieldOverlayRule } from "@coreapp/constants";
import { FieldSelectListProps } from "@/Utils/ComponentLib";

const FieldPropertiesSection = ({ actionType, objClassName, onClose, ...props }) => {
  let wsMgr = StateLib.GetWSMgr();
  const [showAlert, setShowAlert] = useState(true);

  const UpdateFieldName = target => {
    if (!target?.eventInfo) return;
    const { fieldDef } = target.eventInfo;
    const fieldName = fieldDef.GetFieldName();
    const rule = props.rule.Clone();
    rule.SetFieldName(fieldName, actionType);
    props.setRule(rule);
  };

  const UpdateFontColor = selectedColor => {
    const rule = props.rule.Clone();
    rule.SetTextColor(selectedColor, actionType);
    props.setRule(rule);
  };

  const UpdateFieldColor = selectedColor => {
    const rule = props.rule.Clone();
    rule.SetCellColor(selectedColor, actionType);
    props.setRule(rule);
  };

  const UpdateFontFormat = formats => {
    const rule = props.rule.Clone();
    rule.SetBoldStyle(formats.includes("bold"), actionType);
    rule.SetItalicStyle(formats.includes("italic"), actionType);
    rule.SetUnderlineStyle(formats.includes("underline"), actionType);
    props.setRule(rule);
  };

  const ToggleFieldProperties = event => {
    const rule = props.rule.Clone();
    const settingToToggle = event.value;
    if (!rule.fActionSet[actionType]) rule.fActionSet[actionType] = {};
    const theSection = rule.fActionSet[actionType];
    if (settingToToggle == "fTextColorEnabled" && rule.GetTextColor(actionType) == null) {
      rule.SetTextColor("#000000", actionType);
    }
    if (settingToToggle == "fCellShown" && theSection[settingToToggle] == null)
      theSection[settingToToggle] = false;
    else theSection[settingToToggle] = !theSection[settingToToggle];
    props.setRule(rule);

    return theSection[settingToToggle];
  };

  const cellFontColorOpenCtrlRef = useRef(null);
  const toggleCellFontColorCheckboxProps = {
    id: "toggleFieldProperties-checkbox-textColor",
    label: appstr("Set Font Color"),
    size: "medium",
    checked: props.rule.GetTextColorEnabled(actionType) ?? false,
    onToggle: () => {
      const nextEnabled = ToggleFieldProperties({ value: "fTextColorEnabled" });
      cellFontColorOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };

  const cellFillColorOpenCtrlRef = useRef(null);
  const toggleCellFillColorCheckboxProps = {
    id: "toggleFieldProperties-checkbox-fillColor",
    label: appstr("Set Fill Color"),
    size: "medium",
    checked: props.rule.GetCellColorEnabled(actionType) ?? false,
    onToggle: () => {
      const nextEnabled = ToggleFieldProperties({ value: "fCellColorEnabled" });
      cellFillColorOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };

  const toggleCellTextStyleCheckboxProps = {
    id: "toggleFieldProperties-checkbox-textStyle",
    label: appstr("Set Text Style"),
    size: "medium",
    checked: props.rule.GetTextStyleEnabled(actionType) ?? false,
    onToggle: () => {
      ToggleFieldProperties({ value: "fTextStyleEnabled" });
    }
  };

  const toggleCellHiddenCheckboxProps = {
    id: "toggleFieldProperties-checkbox-hidden",
    label: appstr("Hide Field"),
    size: "medium",
    checked: props.rule.GetCellShown(actionType) === false,
    onToggle: () => {
      ToggleFieldProperties({ value: "fCellShown" });
    }
  };

  const fontFormats = ["bold", "underline", "italic"];

  const fieldName = props.rule.GetFieldName(actionType) ?? "";
  let selectedFormats = [];
  if (props.rule.GetBoldStyle(actionType)) selectedFormats.push("bold");
  if (props.rule.GetItalicStyle(actionType)) selectedFormats.push("italic");
  if (props.rule.GetUnderlineStyle(actionType)) selectedFormats.push("underline");

  let cellColor = wsMgr?.SurveySelection(enumUpdateProperty.cellFillColor);
  let textColor = wsMgr?.SurveySelection(enumUpdateProperty.cellFontColor);
  let cellBold = wsMgr?.SurveySelection(enumUpdateProperty.cellTextBold); //gives a true/false for if box is bolded/italic underlined
  let cellItalic = wsMgr?.SurveySelection(enumUpdateProperty.cellTextItalic);
  let cellUnderline = wsMgr?.SurveySelection(enumUpdateProperty.cellTextUnderline);
  cellColor = cellColor !== null && cellColor?.fFillMode !== 2;
  textColor =
    textColor !== null && textColor?.fSelectedColor !== props.rule.GetTextColor(actionType);

  useEffect(() => {
    if (showAlert && (cellColor || textColor || cellBold || cellItalic || cellUnderline)) {
      wsMgr.CreateReactAlert(
        appstr("Conflicting field format changes"),
        appstr("fieldFormatWarning"),
        () => {
          setShowAlert(false);
        },
        () => onClose(),
        appstr("Continue"),
        appstr("Cancel")
      );
    }
  }, []);

  return (
    <Fragment>
      <Select
        optionSize={"small"}
        id={"select-criteria-type-button-"}
        placeholder={appstr("Field") + ":"}
        value={`${objClassName}-${fieldName}`}
        popoverProps={{
          flipOptions: {
            mainAxis: false
          }
        }}
        listProps={FieldSelectListProps({
          onSelect: UpdateFieldName
        })}
        options={BuildSystemFieldOptions(undefined, undefined, undefined, undefined, undefined, [
          objClassName
        ])}
      />
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleCellFontColorCheckboxProps} />
        <CompositeColorPicker
          openCtrlRef={cellFontColorOpenCtrlRef}
          enabled={!!props.rule.GetTextColorEnabled(actionType)}
          trigger={
            <ColorTrigger
              iconName="pen"
              size={"medium"}
              disabled={!props.rule.GetTextColorEnabled(actionType)}
            />
          }
          onChange={UpdateFontColor}
          currentColor={props.rule.GetTextColor(actionType)}
          showNoFill={false}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleCellFillColorCheckboxProps} />
        <CompositeColorPicker
          openCtrlRef={cellFillColorOpenCtrlRef}
          enabled={!!props.rule.GetCellColorEnabled(actionType)}
          trigger={
            <ColorTrigger
              iconName="fill-drip"
              size={"medium"}
              disabled={!props.rule.GetCellColorEnabled(actionType)}
            />
          }
          onChange={UpdateFieldColor}
          currentColor={props.rule.GetCellColor(actionType)}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleCellTextStyleCheckboxProps} />
        <ToggleButtonGroup
          value={selectedFormats}
          onChange={UpdateFontFormat}
          exclusive={false}
          enforceValueSet={false}
          spacing={12}
        >
          {fontFormats.map(item => (
            <ToggleButton
              key={item}
              icon={item}
              value={item}
              disabled={!props.rule.GetTextStyleEnabled(actionType)}
            />
          ))}
        </ToggleButtonGroup>
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleCellHiddenCheckboxProps} />
      </span>
    </Fragment>
  );
};

export default FieldPropertiesSection;
